import { queryOptions } from '@tanstack/react-query';
import { getModelCatalog, getVersion, listVersions } from './agent-definitions.api';
import { AGENT_BUILDER_QUERY_KEYS } from './query-keys';
import { listConversationSummaries } from './conversation-summaries.api';
import { getDataEntry, listSavedData } from './data-entries.api';

/** The models with the one an agent runs on when it names none. */
export const modelCatalogQueryOptions = () =>
  queryOptions({ queryKey: AGENT_BUILDER_QUERY_KEYS.modelCatalog(), queryFn: getModelCatalog });

const catalogModels = (catalog: Awaited<ReturnType<typeof getModelCatalog>>) => catalog.data;

/** The models an agent can run on, with their list prices: the catalogue's list, one request. */
export const modelsQueryOptions = () =>
  queryOptions({ ...modelCatalogQueryOptions(), select: catalogModels });

/** A definition's published versions. */
export const versionsQueryOptions = (id: string) =>
  queryOptions({
    queryKey: AGENT_BUILDER_QUERY_KEYS.versions(id),
    queryFn: () => listVersions(id),
  });

/** One published version, which never changes once published. */
export const versionQueryOptions = (id: string, version: number) =>
  queryOptions({
    queryKey: AGENT_BUILDER_QUERY_KEYS.version(id, version),
    queryFn: () => getVersion(id, version),
    staleTime: Infinity,
  });

/**
 * Where a conversation was summarized. Kept until a compaction is reported, by a turn's stream or
 * by Compact now, either of which invalidates it.
 */
export const conversationSummariesQueryOptions = (contextId: string) =>
  queryOptions({
    queryKey: AGENT_BUILDER_QUERY_KEYS.summaries(contextId),
    queryFn: () => listConversationSummaries(contextId),
    staleTime: Infinity,
  });

/** What the agent saved in a test conversation; read again whenever one of its turns ends. */
export const savedDataQueryOptions = (contextId: string) =>
  queryOptions({
    queryKey: AGENT_BUILDER_QUERY_KEYS.dataEntries(contextId),
    queryFn: () => listSavedData(contextId),
    staleTime: Infinity,
  });

/** One kept entry's value; a key saved again is read afresh with the list it was listed in. */
export const dataEntryQueryOptions = (contextId: string, key: string) =>
  queryOptions({
    queryKey: AGENT_BUILDER_QUERY_KEYS.dataEntry(contextId, key),
    queryFn: () => getDataEntry(contextId, key),
    staleTime: Infinity,
  });
